import {addDays, format} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'
import {
    type AssignmentStripAssignment,
    DISPATCH_TYPEAHEAD_ASSIGNMENT_STRIP_DATE_RANGE,
} from 'src/assignments/assignment-strip-utils'
import {parseApiDate} from 'src/jobs/job-schedule-format'

import type {
    DispatchAssignment,
    DispatchJobSchedule,
    DispatchTimelineData,
    JobTimeSpan,
    UnassignedJob,
} from './types'

function boardWindowAnchor(displayData: DispatchTimelineData): Date {
    return displayData.scheduleWindowStart != null
        ? parseApiDate(displayData.scheduleWindowStart)
        : businessCalendarDate()
}

function spanToDateRange(
    span: JobTimeSpan,
    windowStart: Date,
    scheduleKind: UnassignedJob['scheduleKind'],
): {startDate: string; endDate: string | null} {
    const start = addDays(windowStart, span.dayOffset ?? 0)
    const startDate = format(start, 'yyyy-MM-dd')
    if (span.openEnded) {
        return {startDate, endDate: null}
    }
    const daySpan =
        span.daySpan != null && span.daySpan > 0 ? span.daySpan : scheduleKind === 'two_day' ? 2 : 1
    const end = addDays(start, Math.max(0, daySpan - 1))
    return {
        startDate,
        endDate: format(end, 'yyyy-MM-dd'),
    }
}

/** Calendar range for a board job — used to highlight conflict days in the picker sparkline. */
export function boardJobScheduleDateRange(
    job: UnassignedJob,
    displayData?: DispatchTimelineData,
): {startDate: string; endDate: string | null} | null {
    if (job.schedule != null) {
        return {startDate: job.schedule.startDate, endDate: job.schedule.endDate}
    }
    if (job.span == null) {
        return null
    }
    const windowStart =
        displayData != null ? boardWindowAnchor(displayData) : businessCalendarDate()
    return spanToDateRange(job.span, windowStart, job.scheduleKind)
}

function assignmentRow(
    id: string,
    jobId: string,
    jobName: string,
    span: JobTimeSpan,
    windowStart: Date,
    scheduleKind: UnassignedJob['scheduleKind'],
    schedule?: DispatchJobSchedule,
): AssignmentStripAssignment {
    const {startDate, endDate} =
        schedule != null
            ? {startDate: schedule.startDate, endDate: schedule.endDate}
            : spanToDateRange(span, windowStart, scheduleKind)
    return {
        id,
        jobId,
        jobName,
        startDate,
        startTime: null,
        endDate,
        endTime: null,
    }
}

function jobUsesResource(job: UnassignedJob, resourceId: string): boolean {
    return job.allocations.some((allocation) => allocation.resourceId === resourceId)
}

function committedAssignmentUsesResource(
    assignment: DispatchAssignment,
    resourceId: string,
): boolean {
    if (assignment.resourceId === resourceId) {
        return true
    }

    return (
        assignment.allocations?.some((allocation) => allocation.resourceId === resourceId) ?? false
    )
}

/** Calendar date for a job's scheduled start on the dispatch board. */
export function referenceDateForBoardJob(
    job: UnassignedJob,
    displayData?: DispatchTimelineData,
    today: Date = businessCalendarDate(),
): Date {
    const schedule = job.schedule
    if (schedule != null) {
        const start = parseApiDate(schedule.startDate)
        const end = schedule.endDate != null ? parseApiDate(schedule.endDate) : null
        if (start < today && (end == null || end >= today)) {
            return today
        }

        return start
    }
    const windowStart =
        displayData != null ? boardWindowAnchor(displayData) : businessCalendarDate()
    return addDays(windowStart, job.span?.dayOffset ?? 0)
}

/** Map board jobs for one resource into assignment-strip rows. */
export function boardAssignmentsForResourceStrip(
    displayData: DispatchTimelineData,
    resourceId: string,
): AssignmentStripAssignment[] {
    const windowStart = boardWindowAnchor(displayData)

    const fromCommitted = displayData.assignments.flatMap((assignment) => {
        if (!committedAssignmentUsesResource(assignment, resourceId)) {
            return []
        }

        return [
            assignmentRow(
                `${assignment.id}-${resourceId}`,
                assignment.sourceJobId ?? assignment.id,
                assignment.label,
                assignment.span,
                windowStart,
                assignment.scheduleKind,
                assignment.schedule,
            ),
        ]
    })

    const fromQueue = displayData.unassignedJobs.flatMap((job) => {
        if (job.span == null || !jobUsesResource(job, resourceId)) {
            return []
        }

        return [
            assignmentRow(
                `queue-${job.id}-${resourceId}`,
                job.id,
                job.label,
                job.span,
                windowStart,
                job.scheduleKind,
                job.schedule,
            ),
        ]
    })

    return [...fromCommitted, ...fromQueue]
}

export const DISPATCH_TYPEAHEAD_STRIP_DATE_RANGE = DISPATCH_TYPEAHEAD_ASSIGNMENT_STRIP_DATE_RANGE
